import dayjs from 'dayjs';
import type { BulkResult } from '@/services/hrms.service';
import { downloadXlsx } from '@/reports/utils/exportTable';
import { useAuthStore } from '@/store/authStore';
import { formatMoney, formatNumber } from '@/utils/format';

/** UI affordances only — every action is re-checked by the backend `authorize` guards. */
export function useRecruitAccess() {
  const user = useAuthStore((s) => s.user);
  const has = (code: string) => Boolean(user?.is_super_admin) || Boolean(user?.permissions?.includes(code));
  return {
    /** False for approvers who reach a requisition / offer only through their approval inbox. */
    canView: ['hr.recruit.view', 'hr.recruit.manage', 'hr.onboarding.manage', 'hr.view'].some(has),
    canManage: has('hr.recruit.manage'),
    canConvert: has('hr.recruit.convert'),
    canOnboard: has('hr.onboarding.manage') || has('hr.recruit.manage'),
  };
}

/** Excel export of a recruit register; `cols` are [field, header] pairs. */
export function exportRows(filename: string, cols: Array<[string, string]>, rows: Record<string, unknown>[]) {
  return downloadXlsx(
    filename,
    cols.map(([key, title]) => ({ key, title, dataIndex: key })),
    rows
  );
}

/** Server message for recruit API errors (AppError returns `{ message, code, details }`). */
export function recruitErrorMessage(e: any, fallback = 'Action failed'): string {
  return e?.response?.data?.message || e?.message || fallback;
}

/** JSON columns read via raw SQL arrive as strings on MariaDB. */
export function parseJsonArray<T = any>(v: unknown): T[] {
  if (Array.isArray(v)) return v as T[];
  if (typeof v === 'string' && v) {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function fmtDate(v?: string | Date | null): string {
  return v ? dayjs(v).format('DD MMM YYYY') : '—';
}

export function fmtDateTime(v?: string | Date | null): string {
  return v ? dayjs(v).format('DD MMM YYYY HH:mm') : '—';
}

export function fmtMoney(amount: unknown, currency?: string | null): string {
  return formatMoney(amount, { currency: currency || 'AED', nullDisplay: '—' });
}

export function fmtCount(n: unknown, fractionDigits = 0): string {
  return formatNumber(n, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits, nullDisplay: '—' });
}

export function bulkSummary(label: string, r: BulkResult): { type: 'success' | 'warning'; text: string } {
  if (!r.failed?.length) return { type: 'success', text: `${label}: ${r.succeeded} updated` };
  const first = r.failed[0]?.error;
  return {
    type: 'warning',
    text: `${label}: ${r.succeeded} updated, ${r.failed.length} skipped${first ? ` (e.g. ${first})` : ''}`,
  };
}

export const APPLICATION_STAGES = [
  'Applied',
  'Screening',
  'Interview',
  'Offer',
  'Hired',
  'TalentPool',
  'Rejected',
  'Withdrawn',
] as const;

/** Manual moves allowed from each stage (mirrors backend recruitment-rules; Hired only via convert). */
export const APPLICATION_NEXT: Record<string, string[]> = {
  Applied: ['Screening', 'Interview', 'TalentPool', 'Rejected', 'Withdrawn'],
  Screening: ['Applied', 'Interview', 'TalentPool', 'Rejected', 'Withdrawn'],
  Interview: ['Screening', 'Offer', 'TalentPool', 'Rejected', 'Withdrawn'],
  Offer: ['Interview', 'TalentPool', 'Rejected', 'Withdrawn'],
  TalentPool: ['Screening', 'Interview', 'Rejected'],
  Rejected: ['TalentPool', 'Screening'],
  Withdrawn: ['TalentPool', 'Screening'],
  Hired: [],
};

export const STAGE_LABEL: Record<string, string> = {
  TalentPool: 'Talent pool',
  PendingApproval: 'Pending approval',
  OnHold: 'On hold',
  NoShow: 'No-show',
  InProgress: 'In progress',
};

export const stageLabel = (s?: string) => (s ? STAGE_LABEL[s] || s : '—');

export const OWNER_ROLE_LABEL: Record<string, string> = {
  HR: 'HR',
  IT: 'IT',
  Admin: 'Admin',
  Finance: 'Finance',
  LineManager: 'Line manager',
  NewHire: 'New hire',
};

export const RECOMMENDATIONS = [
  { value: 'StrongHire', label: 'Strong hire', color: 'green' },
  { value: 'Hire', label: 'Hire', color: 'cyan' },
  { value: 'Hold', label: 'Hold', color: 'gold' },
  { value: 'Reject', label: 'Reject', color: 'red' },
];

export const CANDIDATE_SOURCES = ['Careers', 'Referral', 'Agency', 'LinkedIn', 'Job board', 'Walk-in', 'Internal', 'Import', 'Other'];
